import { useState, useRef, useEffect, useCallback } from "react"

interface CanvasProps {
    URL: string,
    onBoxUpdate: (newBoxArray:number[]|null) => void
    style?: Object,
    boxArray?: number[],
    width?: number,
    height?: number,
}

interface Point {
    x: number,
    y: number
}

interface Dimensions {
    width: number,
    height: number
}

interface BoxInterface {
    left: number,
    top: number,
    right: number,
    bottom: number
}

interface Rect {
    tl: Point,
    dims: Dimensions
}

interface ClickedPoint {
    pt: Point,
    cc: number // Closest Corner (CC)
}

const Corners = Object.freeze({
    TOP_LEFT: 0,
    TOP_RIGHT: 1,
    BOTTOM_RIGHT: 2,
    BOTTOM_LEFT: 3
})

class VehicleBox {

    _box:BoxInterface;
    _dims:Dimensions;

    constructor(left:number,top:number,right:number,bottom:number,width:number,height:number) {
        this._box = {left,top,right,bottom};
        this._dims = {width,height};
    }

    get left() { return this._box.left*this._dims.width};
    get top() { return this._box.top*this._dims.height};
    get right() { return this._box.right*this._dims.width};
    get bottom() { return this._box.bottom*this._dims.height};
    get width() { return this.right-this.left};
    get height() { return this.bottom-this.top};

    get tl():Point { return {x: this.left, y: this.top}};
    get tr():Point { return {x: this.right, y: this.top}};
    get br():Point { return {x: this.right, y: this.bottom}};
    get bl():Point { return {x: this.left, y: this.bottom}};

    setLeft(v:number) { this._box.left = v/this._dims.width };
    setTop(v:number) { this._box.top = v/this._dims.height };
    setRight(v:number) { this._box.right = v/this._dims.width };
    setBottom(v:number) { this._box.bottom = v/this._dims.height };

    setWidth(w:number) { this._dims.width = w };
    setHeight(h:number) { this._dims.height = h };

    getArray():number[] {
        return [
            this._box.left,
            this._box.top,
            this._box.right,
            this._box.bottom
        ]
    }

    getRect():Rect {
        return {
            tl: {x: this.left, y: this.top},
            dims: {width: this.width, height: this.height}
        }
    }

    static fromBoxArray(boxArray:number[],width:number=1.,height:number=1.) {
        const [l,t,r,b] = boxArray;
        return new VehicleBox(l,t,r,b,width,height);
    }
}

function euc(pt1:Point,pt2:Point) {
    return Math.sqrt(Math.pow(pt1.x-pt2.x,2)+Math.pow(pt1.y-pt2.y,2));
}

function closestCorner(pt:Point,box:VehicleBox) {
    const corners = [box.tl,box.tr,box.br,box.bl];
    const dists = corners.map((_pt) => euc(pt,_pt));
    return dists.indexOf(Math.min(...dists));
}

const getPoint = (evt:React.MouseEvent):Point => {
    return {
        x: evt.nativeEvent.offsetX,
        y: evt.nativeEvent.offsetY
    }
}


export const MMCVehicleCanvas:React.FC<CanvasProps> = ({
    URL,
    boxArray,
    style,
    width,
    height,
    onBoxUpdate
}) => {

    const [clickedPos,setClickedPos] = useState<ClickedPoint|null>(null);
    const [vehicleBox,setVehicleBox] = useState<VehicleBox|null>(null);
    const [imgLoaded,setImgLoaded] = useState<boolean>(false);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const imgRef = useRef<HTMLImageElement | null>(null);

    useEffect(()=>{
        if (canvasRef.current && boxArray) {
            setVehicleBox(
                VehicleBox.fromBoxArray(
                    boxArray,
                    canvasRef.current.width,
                    canvasRef.current.height
                )
            )
        } else {
            setVehicleBox(null);
        }
    },[canvasRef,boxArray]);

    // Load image with proper cleanup to prevent memory leaks
    useEffect(() => {
        setImgLoaded(false);
        const img = new Image();
        img.src = URL;
        img.onload = () => setImgLoaded(true);
        imgRef.current = img;

        return () => {
            // Cleanup: remove event handlers and clear reference
            img.onload = null;
            img.onerror = null;
            imgRef.current = null;
        };
    }, [URL]);

    const getMovement = useCallback( (evt:React.MouseEvent) => {
        const pt = getPoint(evt);
        if (clickedPos)
            return {
                x: pt.x-clickedPos.pt.x,
                y: pt.y-clickedPos.pt.y
            }
        else return pt;
    },[clickedPos])

    const getPredictedBoxPositions = useCallback( (movement:Point,corner:number):number[] => {
        let [l,t,r,b] = [vehicleBox!.left,vehicleBox!.top,vehicleBox!.right,vehicleBox!.bottom];
        switch (corner) {
            case Corners.TOP_LEFT:
                l += movement.x;
                t += movement.y;
                break;
            case Corners.TOP_RIGHT:
                r += movement.x;
                t += movement.y;
                break;
            case Corners.BOTTOM_RIGHT:
                r += movement.x;
                b += movement.y;
                break;
            case Corners.BOTTOM_LEFT:
                l += movement.x;
                b += movement.y;
                break;
        }
        return [l,t,r,b];
    },[vehicleBox])

    const draw = (movement:Point|null=null) => {
        const canvas = canvasRef.current;
        const img = imgRef.current;
        if (!canvas || !img) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        ctx.drawImage(img,0,0,img.width,img.height,0,0,canvas.width,canvas.height);

        if (vehicleBox) {

            let [l,t,r,b] = [vehicleBox.left,vehicleBox.top,vehicleBox.right,vehicleBox.bottom]
            if (clickedPos && movement)
                [l,t,r,b] = getPredictedBoxPositions(movement,clickedPos.cc)

            ctx.strokeStyle = "#FF0000";
            ctx.lineWidth = 4;
            ctx.strokeRect(l,t,r-l,b-t);
        }
    }

    const mouseMove = (evt:React.MouseEvent) => {
        if (!clickedPos) return;
        draw(getMovement(evt));
    }

    const mouseDown = (evt:React.MouseEvent) => {
        if (evt.button !== 0) return;
        if (!canvasRef.current) return;
        if (!vehicleBox) {
            setVehicleBox(VehicleBox.fromBoxArray(
                [.25,.25,.75,.75],
                canvasRef.current.width,
                canvasRef.current.height
            ));
        } else {
            const pt = getPoint(evt);
            const cc = closestCorner(pt,vehicleBox);
            setClickedPos({pt,cc});
        }
    }

    const mouseUp = (evt:React.MouseEvent) => {
        if (vehicleBox) {
            if (evt.button === 1) { // Wheel button
                setVehicleBox(null);
                onBoxUpdate(null);
            } else {
                if (clickedPos) {
                    const [l,t,r,b] = getPredictedBoxPositions(getMovement(evt),clickedPos.cc)
                    vehicleBox.setLeft(l);
                    vehicleBox.setTop(t);
                    vehicleBox.setRight(r);
                    vehicleBox.setBottom(b);
                    setClickedPos(null);
                }
                onBoxUpdate(vehicleBox.getArray());
            }
        }
    }

    // Redraw when image is loaded
    if (imgLoaded && canvasRef.current && imgRef.current) draw();

    return (<>
        <canvas
            width={width}
            height={height}
            ref={canvasRef}
            onMouseMove={mouseMove}
            onMouseDown={mouseDown}
            onMouseUp={mouseUp}
            style={style}
            role="img"
            aria-label="Vehicle image with adjustable bounding box. Click and drag corners to resize the box."
            tabIndex={0}
        />
    </>)
}

export default MMCVehicleCanvas;